import React from 'react';
import { Link } from 'react-router-dom';
import '@/styles/seo-landing.css';

/**
 * Shared shell for the high-intent SEO landing pages. Keeps the visual
 * language consistent across the set while letting each page carry its
 * own distinct copy. Renders nothing route-specific itself — copy comes
 * in via props so each route stays distinct for Google.
 *
 * Required content shape:
 *   eyebrow:     short uppercase route label
 *   h1:          page H1 (unique per page)
 *   intro:       lead paragraph
 *   sections:    [{ heading, paras: string[], quiet?: bool }]
 *   safetyNote:  short paragraph (subtle trust mention)
 *   testid:      unique data-testid for the page wrapper
 */
export default function SeoLandingPage({ eyebrow, h1, intro, sections, safetyNote, testid }) {
  return (
    <main className="seo-landing" data-testid={testid}>
      <article className="seo-container">
        <p className="seo-eyebrow">{eyebrow}</p>
        <h1 className="seo-h1">{h1}</h1>
        <p className="seo-intro">{intro}</p>

        <div className="seo-divider" aria-hidden="true" />

        {sections.map((s, i) => (
          <section
            key={i}
            className={`seo-section ${s.quiet ? 'seo-section--quiet' : ''}`}
            data-testid={`${testid}-section-${i + 1}`}
          >
            <h2>{s.heading}</h2>
            {s.paras.map((p, j) => (
              <p key={j}>{p}</p>
            ))}
          </section>
        ))}

        <p className="seo-foot" data-testid={`${testid}-safety`}>
          {safetyNote}{' '}
          <Link to="/safety">Read about safety on NYMPHÉ</Link>.
        </p>

        <div className="seo-cta-row">
          <Link
            to="/access"
            className="seo-cta-primary"
            data-testid={`${testid}-cta-access`}
          >
            Request early access
          </Link>
          <Link
            to="/the-app"
            className="seo-cta-secondary"
            data-testid={`${testid}-cta-the-app`}
          >
            See how the app works &rarr;
          </Link>
        </div>
      </article>
    </main>
  );
}
